import { DateTime } from "luxon";
import { CarePlan, TextArea, VisitByIdQuery } from "../api/generated/graphql";
import { Activity, Status, ParsedVisit } from "../types";
import { activitiesForVisit } from "./activitiesUtils";
import { match } from "ts-pattern";
import { useMemo } from "react";
import { useCareContext } from "../providers";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";

export const getImportantInformationFromCarePlan = (
  carePlan: Omit<CarePlan, "id"> | undefined,
) => {
  if (!carePlan || !carePlan.fields) {
    return undefined;
  }

  return (
    carePlan?.fields.filter((f) => f.__typename === "TextArea") as TextArea[]
  ).find((f) => f.id === "carePlan.importantInformation")?.value;
};

export type VisitInfoWithActivities = ParsedVisit & {
  activities: Activity[];
};

export const getStatus = (
  clockInTime: string | null,
  clockOutTime: string | null,
): Status => {
  if (!clockInTime && !clockOutTime) {
    return "pending";
  }

  if (clockInTime && !clockOutTime) {
    return "clockedIn";
  }
  return "clockedOut";
};

export const useVisitInfo = (
  visit: NonNullable<VisitByIdQuery["visitById"]> | undefined | null,
): VisitInfoWithActivities | null => {
  const {
    state: { viewer },
  } = useCareContext();
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);
  const careRecipients = useMemo(() => {
    if (!visit) {
      return [];
    }

    return visit.homeStays
      .map((homeStay) => homeStay.careRecipient)
      .map((careRecipient) => ({
        ...careRecipient,
        activities: careRecipient.activities?.map((activity) => ({
          ...activity,
          assignedFor: careRecipient.id,
          timeOfDayStart: activity.timeOfDayStart
            ? DateTime.fromFormat(activity.timeOfDayStart, "HH:mm")
            : undefined,
        })),
      }));
  }, [visit]);

  const activities = useMemo(() => {
    if (!visit) {
      return [];
    }
    return activitiesForVisit(visit);
  }, [visit]);

  const to = useMemo(() => {
    if (!visit) {
      return DateTime.now();
    }
    return DateTime.fromISO(visit.start).plus({
      minutes: visit.durationMinutes,
    });
  }, [visit]);

  const shouldShowLateClockOutModal = useMemo(() => {
    if (!visit || !to) {
      return false;
    }

    return visit.clockInTime ? DateTime.fromISO(visit.clockInTime) < to : false;
  }, [visit, to]);

  const { name, birthDate, importantInformation, status } = useMemo(() => {
    if (!visit) {
      return {
        name: "",
        birthDate: null,
        importantInformation: null,
        status: null,
      };
    }
    return match(visit.subject)
      .with({ __typename: "CareRecipient" }, (sub) => ({
        name: nameOrderFn(sub),
        birthDate: sub.birthDate,
        importantInformation: getImportantInformationFromCarePlan(sub.carePlan),
        status: getStatus(visit.clockInTime, visit.clockOutTime),
      }))
      .with({ __typename: "CareHome" }, (sub) => {
        const visitor = visit.visitorsV2.find((v) => v.id === viewer?.id);
        return {
          name: sub.name,
          birthDate: null,
          importantInformation: null,
          status: getStatus(
            visitor?.clockInTime ?? null,
            visitor?.clockOutTime ?? null,
          ),
        };
      })
      .otherwise(() => ({
        name: "",
        birthDate: null,
        importantInformation: null,
        status: null,
      }));
  }, [visit, nameOrderFn, viewer]);

  if (!visit) {
    return null;
  }

  const visitInfo: VisitInfoWithActivities = {
    id: visit.id,
    from: DateTime.fromISO(visit.start),
    to,
    mileage:
      Number(visit.mileage?.drivenForCareRecipientReported) +
      Number(visit.mileage?.drivenToLocationReported),
    clockIn: visit.clockInTime ? DateTime.fromISO(visit.clockInTime) : null,
    clockOut: visit.clockOutTime ? DateTime.fromISO(visit.clockOutTime) : null,
    cancelledAt: visit.cancelledAt,
    expenses: visit.expenses[0]?.reportedAmount,
    status: status ?? "pending",
    name,
    activities,
    birthDate,
    importantInformation,
    shouldShowLateClockOutModal,
    careRecipients,
    subject: visit.subject,
    visitors: visit.visitorsV2,
  };

  return visitInfo;
};
